import { getBusinessObject } from 'bpmn-js/lib/util/ModelUtil';

/**
 * Module-level registry mapping a `camunda:modelerTemplate` id (which we use
 * to tag tasks created from an element template) to the icon URL declared by
 * that template.
 *
 * We intentionally do NOT persist the icon URL on the BPMN element itself
 * — Camunda Engine 7.x logs "unknown attribute" warnings for
 * `camunda:modelerTemplateIcon` and (more importantly) for any homegrown
 * attribute like `camunda:customElement`. Some deployments treat those
 * warnings as fatal during deploy. Keeping the icon out-of-band fixes both.
 *
 * Population: when the user applies an element template via the property
 * panel (see `SimpleBpmnEditor`'s template-application path), we call
 * `registerTemplateIcon(template.topic, template.modelerTemplateIcon)`.
 * Anyone loading a diagram that already used the legacy attribute also
 * benefits — the renderer falls back to reading the businessObject below.
 */
const templateIconRegistry = new Map<string, string>();

/** Register an icon for a given template id (idempotent — last write wins). */
export function registerTemplateIcon(templateId: string, iconUrl: string): void {
    if (!templateId || !iconUrl) return;
    templateIconRegistry.set(templateId, iconUrl);
}

/** Inspect a registered icon. Useful for tests / debugging. */
export function getRegisteredTemplateIcon(templateId: string): string | undefined {
    if (!templateId) return undefined;
    return templateIconRegistry.get(templateId);
}

/**
 * Get the icon for a BPMN element. Resolution priority:
 *   1. The legacy `camunda:modelerTemplateIcon` attribute on the element
 *      (backward compatibility with diagrams saved by the old code path).
 *   2. The `camunda:modelerTemplate` id on the element, looked up in the
 *      module-level registry (the new path — keeps the icon out of the XML).
 *
 * @param element - The diagram element
 * @param iconProperty - Optional override of the legacy attribute name
 * @returns The icon URL or undefined if not found
 */
export function getModelerTemplateIcon(element: any, iconProperty?: string): string | undefined {
    const bo = getBusinessObject(element);
    const legacyAttr = iconProperty || 'camunda:modelerTemplateIcon';
    const legacy = bo.get(legacyAttr);
    if (legacy) return legacy as string;

    const templateId =
        bo.get('camunda:modelerTemplate') ||
        // The very old code used `camunda:customElement` to stash the id —
        // still tolerated here so existing diagrams don't lose their icons.
        bo.get('camunda:customElement');
    if (typeof templateId === 'string' && templateId) {
        return templateIconRegistry.get(templateId);
    }
    return undefined;
}
